import { resolveNativeNavigationForWebPath } from "./resolveNativeNavigationForWebPath.js";
import { serializeMobileWebBridgeMessage } from "./mobileWebBridgeProtocol.js";

/**
 * @returns {boolean}
 */
export function isMobileWebViewHost() {
  try {
    return (
      typeof window !== "undefined" && window.__TTB_MOBILE_WEBVIEW__ === true
    );
  } catch {
    return false;
  }
}

/**
 * @param {string} payload
 * @returns {boolean}
 */
function __postToNative(payload) {
  try {
    const bridge = window.ReactNativeWebView;
    if (!bridge || typeof bridge.postMessage !== "function") return false;
    bridge.postMessage(payload);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {import('./mobileWebBridgeProtocol.js').MobileWebBridgeType} type
 * @param {Record<string, unknown>} [fields]
 * @returns {boolean}
 */
export function postMobileWebBridgeMessage(type, fields = {}) {
  if (!isMobileWebViewHost()) return false;
  return __postToNative(serializeMobileWebBridgeMessage(type, fields));
}

export function notifyMobileWebBridgeReady() {
  return postMobileWebBridgeMessage("ready");
}

export function requestNativeGoBack() {
  return postMobileWebBridgeMessage("goBack");
}

export function requestNativeOpenHub() {
  return postMobileWebBridgeMessage("openHub");
}

/**
 * @param {{ governanceKey: string, params?: Record<string, unknown> }} target
 * @returns {boolean}
 */
export function requestNativeNavigateApp({ governanceKey, params }) {
  return postMobileWebBridgeMessage("navigateNative", {
    governanceKey: String(governanceKey || "").trim(),
    params: params && typeof params === "object" ? params : undefined,
  });
}

/**
 * @param {{ webPath: string, titleKey?: string }} target
 * @returns {boolean}
 */
export function requestNativeNavigateWeb({ webPath, titleKey }) {
  const path = String(webPath || "").trim();
  if (!path) return false;
  return postMobileWebBridgeMessage("navigateWeb", {
    webPath: path.startsWith("/") ? path : `/${path}`,
    titleKey: titleKey ? String(titleKey).trim() : undefined,
  });
}

/**
 * iOS-WebView: nativen Screen bevorzugen, sonst WebView-Route wechseln.
 *
 * @param {string} webPath
 * @returns {boolean}
 */
export function tryRedirectMobileWebViewNavigation(webPath) {
  if (!isMobileWebViewHost()) return false;

  const raw = String(webPath || "").trim();
  if (!raw) return false;

  const nativeTarget = resolveNativeNavigationForWebPath(raw);
  if (nativeTarget && requestNativeNavigateApp(nativeTarget)) {
    return true;
  }

  return requestNativeNavigateWeb({ webPath: raw });
}

/**
 * Navigation mit Mobile-Bridge: Hub/App-Root → nativ, sonst Web-Router.
 *
 * @param {(path: string, ...args: unknown[]) => unknown} navigateTo
 * @param {string} path
 * @param {...unknown} args
 * @returns {unknown}
 */
export function navigateWithMobileBridge(navigateTo, path, ...args) {
  const raw = String(path || "").trim();
  if (!raw) return navigateTo(path, ...args);

  const pathname = raw.split("?")[0].split("#")[0] || "/";
  if (pathname === "/hub" || pathname === "/home" || pathname === "/") {
    if (requestNativeOpenHub()) return undefined;
  }

  const nativeTarget = resolveNativeNavigationForWebPath(raw);
  if (nativeTarget && requestNativeNavigateApp(nativeTarget)) {
    return undefined;
  }

  return navigateTo(path, ...args);
}

/**
 * Bridge-Client in der Web-App aktivieren (WebView).
 *
 * @param {{ navigateTo?: (path: string, ...args: unknown[]) => unknown }} [opts]
 * @returns {{ navigateTo?: (path: string, ...args: unknown[]) => unknown } | null}
 */
export function installMobileWebBridgeClient(opts = {}) {
  if (!isMobileWebViewHost()) return null;

  notifyMobileWebBridgeReady();

  const baseNavigate =
    typeof opts.navigateTo === "function" ? opts.navigateTo : null;
  if (!baseNavigate)
    return { navigateTo: navigateWithMobileBridge.bind(null, () => {}) };

  const bridgedNavigateTo = (path, ...args) =>
    navigateWithMobileBridge(baseNavigate, path, ...args);

  try {
    window.__TTB_MOBILE_NAVIGATE__ = bridgedNavigateTo;
  } catch (_) {}

  return { navigateTo: bridgedNavigateTo };
}
